import React from "react";
import { Modal } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { formatMomentDate } from "../../../utils/dateHelper";
import { Calendar, Phone, Info } from "lucide-react";
import withModal from "../../../components/withModal";
import { routes } from "../../../routes/pageRoutes";
import CommonButton from "../../../components/commonButton";

const ViewRequirementModalInner = ({ data, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();

    if (!data) return null;

    const user = data?.user || {};
    const category = data?.category || {};
    const state = data?.state || {};
    const city = data?.city || {};
    const status = data?.status || "open";

    const getInitials = (first, last) => {
        const f = first ? first[0] : "";
        const l = last ? last[0] : "";
        return (f + l).toUpperCase() || "U";
    };

    const handleUserRedirect = () => {
        if (user?.id) {
            const targetPath = routes.viewUser.replace(":id", user.id);
            if (location.pathname === targetPath) {
                onClose();
            } else {
                navigate(targetPath);
            }
        }
    };

    const isClosed = status.toLowerCase() === "closed";

    return (
        <>
            <Modal.Body className="p-4">
                {/* Header Section */}
                <div className="mb-4">
                    <div>
                        <h4 className="mb-0 fw-bold" style={{ color: "#1F2937", fontSize: "20px" }}>Requirement Details</h4>
                        <p className="mb-0 text-muted small">View complete requirement information</p>
                    </div>
                </div>

                {/* Metadata Section */}
                <div className="d-flex justify-content-end gap-4 mb-4 text-secondary pb-3 border-bottom" style={{ fontSize: "13px" }}>
                    <div className="d-flex align-items-center gap-2">
                        <Calendar size={16} className="text-muted" />
                        <div>
                            <div className="text-muted small">Created on</div>
                            <div className="fw-semibold text-dark">{data.created_at ? formatMomentDate(data.created_at, "DD MMM YYYY, hh:mm A") : "-"}</div>
                        </div>
                    </div>
                    <div style={{ width: "1px", backgroundColor: "#E5E7EB" }}></div>
                    <div>
                        <div className="text-muted small">Requirement ID</div>
                        <div className="fw-semibold text-dark">#{data.uid || data.id || "-"}</div>
                    </div>
                </div>

                {/* Requester Information Card */}
                <div className="card border rounded-3 mb-4 overflow-hidden" style={{ borderColor: "#E5E7EB" }}>
                    <div className="card-header bg-white border-0 pt-3 pb-2 px-3">
                        <span className="fw-semibold text-dark" style={{ fontSize: "14px" }}>Requester Information</span>
                    </div>
                    <div className="card-body pt-1 pb-3 px-3 d-flex align-items-center gap-3">
                        {user.profile?.profile_photo?.url ? (
                            <img src={user.profile.profile_photo.url} alt="Requester"
                                className="rounded-circle object-fit-cover cursor-pointer shadow-sm"
                                style={{ width: "48px", height: "48px" }}
                                onClick={handleUserRedirect} />
                        ) : (
                            <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-primary cursor-pointer shadow-sm"
                                style={{ width: "48px", height: "48px", backgroundColor: "#DBEAFE", fontSize: "16px" }}
                                onClick={handleUserRedirect}>
                                {getInitials(user.first_name, user.last_name)}
                            </div>
                        )}
                        <div>
                            <h5 className="mb-0 fw-bold text-dark cursor-pointer" style={{ fontSize: "15px" }} onClick={handleUserRedirect}>
                                {user.first_name || "-"} {user.last_name || ""}
                            </h5>
                            <div className="d-flex align-items-center gap-2 text-secondary small mt-1">
                                <Phone size={13} className="text-muted" />
                                <span>{user.mobile_number || "-"}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Requirement Details Card */}
                <div className="card border rounded-3 mb-4 overflow-hidden" style={{ borderColor: "#E5E7EB" }}>
                    <div className="card-header bg-white border-0 pt-3 pb-2 px-3">
                        <span className="fw-semibold text-dark" style={{ fontSize: "14px" }}>Requirement Details</span>
                    </div>
                    <div className="card-body pt-1 pb-3 px-3">
                        <div className="row g-3">
                            <div className="col-6">
                                <div className="text-muted small">Title</div>
                                <div className="fw-bold text-dark mt-1" style={{ fontSize: "14px" }}>{data.title || "-"}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-muted small">Category</div>
                                <div className="fw-bold text-dark mt-1" style={{ fontSize: "14px" }}>{category.name || "-"}</div>
                            </div>
                            <div className="col-12 border-top pt-2 mt-3">
                                <div className="text-muted small">Description</div>
                                <div className="text-dark mt-1 text-break lh-base" style={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>
                                    {data.description || "-"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Location Card */}
                <div className="card border rounded-3 mb-4 overflow-hidden" style={{ borderColor: "#E5E7EB" }}>
                    <div className="card-header bg-white border-0 pt-3 pb-2 px-3">
                        <span className="fw-semibold text-dark" style={{ fontSize: "14px" }}>Location</span>
                    </div>
                    <div className="card-body pt-1 pb-3 px-3">
                        <div className="row g-3">
                            <div className="col-6">
                                <div className="text-muted small">State</div>
                                <div className="fw-bold text-dark mt-1" style={{ fontSize: "14px" }}>{state.name || "-"}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-muted small">City</div>
                                <div className="fw-bold text-dark mt-1" style={{ fontSize: "14px" }}>{city.name || "-"}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Status Alert */}
                <div className="rounded-3 p-3 d-flex align-items-center justify-content-between"
                    style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px", backgroundColor: "#F59E0B", color: "#FFFFFF" }}>
                            <Info size={16} />
                        </div>
                        <div>
                            <div className="text-muted small fw-semibold">Status</div>
                            <span className="badge rounded-pill bg-white text-warning border border-warning px-2 py-1 small fw-semibold mt-1" style={{ fontSize: "11px" }}>
                                {!isClosed ? 'Open' : 'Closed'}
                            </span>
                        </div>
                    </div>
                    <div style={{ width: "1px", height: "35px", backgroundColor: "#FCD34D", margin: "0 20px" }}></div>
                    <div className="flex-grow-1 text-warning-emphasis fw-medium" style={{ fontSize: "13px" }}>
                        {!isClosed 
                            ? "This requirement is currently open and visible to sellers." 
                            : "This requirement is closed and no longer visible to sellers."
                        }
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 pb-4 px-4">
                <CommonButton
                    title="Close"
                    variant="outline"
                    onClick={onClose}
                    type="button"
                />
            </Modal.Footer>
        </>
    );
};

const ViewRequirementModal = withModal(ViewRequirementModalInner);

export default ViewRequirementModal;
